import type { Saga } from '@redux-saga/core';
import { Connect } from 'react-redux';
import { Reducer } from 'redux';
import { SagaMode } from './SagaMode';
import { createSagaInjector } from './createSagaInjector';
import { createStoreCreator } from './createStoreCreator';
import { createApplicationStoreProvider } from './createApplicationStoreProvider';
import { createReducerInjector } from './createReducerInjector';
// @ts-expect-error not typed
import { createStoreConnector } from './createStoreConnector';
import { getStoreEnvOptions, StoreContext, StoreEnvOptions } from './getStoreEnvOptions';

export interface InjectableReducer {
  key: string;
  reducer: Reducer;
}

export interface InjectableSaga {
  key: string;
  saga: Saga;
  mode?: SagaMode;
}

/**
 * Prepare utilities for creating store, injecting saga and etc. by storeKey
 *
 * @param {StoreEnvOptions} [rawStoreEnvOptions]
 * @returns {{ StoreProvider: Component, connectToStore: Function, injectReducer: injectReducer, injectSaga: injectSaga, createStore: Function }}
 */
const createStoreEnvironment = <TContext extends StoreContext>(
  rawStoreEnvOptions?: Partial<StoreEnvOptions<TContext>>,
) => {
  const storeEnvOptions = getStoreEnvOptions(rawStoreEnvOptions);

  return {
    StoreProvider: createApplicationStoreProvider(storeEnvOptions),
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    connectToStore: createStoreConnector(storeEnvOptions) as Connect,

    injectReducer: ({ key, reducer }: InjectableReducer) =>
      createReducerInjector(storeEnvOptions)({ [key]: reducer }),
    injectMultipleReducers: createReducerInjector(storeEnvOptions),

    injectSaga: ({ key, saga, mode }: InjectableSaga) =>
      createSagaInjector(storeEnvOptions)({ [key]: { saga, mode } }),
    injectMultipleSagas: createSagaInjector(storeEnvOptions),

    createStore: createStoreCreator(storeEnvOptions),
    StoreContext: storeEnvOptions.context,
  };
};

export { createStoreEnvironment };
